import {
  createContext,
  type ReactNode,
  use,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";

/**
 * Global ambient audio player.
 *
 * Mounts a single <audio> element above the routes so the onboarding
 * ambience continues playing when the user navigates from /onboarding
 * to /today and survives the boot splash. The boot screen calls
 * `fadeOut()` on dismissal so the audio settles into silence as the
 * splash dissolves (rather than cutting on route change).
 *
 * State machine:
 *   - `isOn` mirrors the user's preference (persisted to localStorage).
 *   - `isPlaying` is the audio element's actual playback state, which
 *     may differ from `isOn` until autoplay clears (browsers gate the
 *     first .play() on a user gesture).
 *
 * UI surfaces (AmbientToggle, etc.) consume this via `useAmbience()`
 * and never own their own audio element. Two concurrent audio tags
 * for the same loop sounded like phasing on hot-reload during dev.
 */

const AUDIO_SRC = "/sounds/onboarding-ambience.mp3";
const SNAP_SRC = "/sounds/hos-snap.mp3";
// The ambient bed sits at this fraction of full across onboarding. Low enough
// to be felt rather than listened to: it is a room tone under the wizard, not
// a soundtrack. It drops further under spoken narration via duck()/unduck() so
// the leveled Aha voiceover always reads clearly above it.
const TARGET_VOLUME = 0.04;
const FADE_IN_MS = 1800;
const FADE_OUT_DEFAULT_MS = 600;
const STORAGE_KEY = "ob-ambient";
/* Playback position survives full navigations (OAuth redirects, reloads) so
 * the bed resumes where it left off instead of restarting. Session-scoped:
 * a fresh tab starts the loop from the top. */
const POSITION_KEY = "thrive_ambience_pos";

/**
 * One note per ignition ripple, on the ripple's own onset.
 *
 * The offsets are the animation-delay values of `.is-igniting .ripple.rN` in
 * boot-screen.css, so the sequence accelerates exactly as the rings do (gaps of
 * 1010, 540, 400, 330, 270, 220, 190ms) and lands on the snap at 3100ms. Change
 * one and the other is wrong, so they are noted in both places.
 *
 * Pitches climb a D major pentatonic: no semitone clashes as they overlap, and
 * it resolves upward into the snap rather than sitting under it.
 */
export const IGNITION_NOTES: ReadonlyArray<{ atMs: number; hz: number }> = [
  { atMs: 0, hz: 293.66 },
  { atMs: 1010, hz: 329.63 },
  { atMs: 1550, hz: 369.99 },
  { atMs: 1950, hz: 440.0 },
  { atMs: 2280, hz: 493.88 },
  { atMs: 2550, hz: 587.33 },
  { atMs: 2770, hz: 659.25 },
  { atMs: 2960, hz: 739.99 },
];
/** Quiet enough to read as the ripples having a voice, not as a countdown. */
const IGNITION_PEAK_GAIN = 0.05;
const IGNITION_NOTE_MS = 620;

/**
 * The arrival. Three beats in order: the climb, then this, then the snap.
 *
 * It lands as the last ignition ring completes at 3380ms and gets a clear beat
 * of its own before the wipe, which is the whole point. At 3000 against a
 * 3100ms wipe it sounded simultaneous with the snap rather than before it, so
 * the phrase had no arrival. Sine plus a quiet octave partial reads as a chime;
 * the triangle used for the climb reads as a note. The long decay carries it
 * under the snap, so the handover ends the phrase instead of cutting it.
 */
export const IGNITION_CHIME = {
  atMs: 3300,
  hz: 1174.66,
  /**
   * Struck-bell decay, and it sets how long the chime is still audible rather
   * than just how long it is scheduled for. An exponential ramp is most of the
   * way down well before it ends: at 1700ms the chime was 39dB below peak by
   * the time the snap fired, which is silence, not a ring. At 2600 it is still
   * present (about 25dB down) after its full beat.
   */
  decayMs: 2600,
  gain: 0.055,
  /** One octave up, well under the fundamental, for air rather than a second note. */
  partialHz: 2349.32,
  partialGain: 0.018,
} as const;

function prefersReducedMotion(): boolean {
  return (
    typeof window !== "undefined" &&
    typeof window.matchMedia === "function" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches
  );
}

function readPosition(): number {
  try {
    const v = Number(sessionStorage.getItem(POSITION_KEY));
    return Number.isFinite(v) && v > 0 ? v : 0;
  } catch {
    return 0;
  }
}

function savePosition(audio: HTMLAudioElement | null): void {
  if (!audio?.src || audio.currentTime <= 0) return;
  try {
    sessionStorage.setItem(POSITION_KEY, String(audio.currentTime));
  } catch {
    // sessionStorage unavailable
  }
}

function readPref(): boolean {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    if (v === "1") return true;
    if (v === "0") return false;
  } catch {
    // localStorage unavailable; treat as default-on
  }
  return true;
}

function writePref(on: boolean): void {
  try {
    localStorage.setItem(STORAGE_KEY, on ? "1" : "0");
  } catch {
    // localStorage unavailable
  }
}

// One in-flight fade per element. A duck/unduck/fade landing mid-fade cancels
// the prior frame loop so they don't fight over audio.volume each frame.
const fadeFrames = new WeakMap<HTMLAudioElement, number>();
function fadeVolume(audio: HTMLAudioElement, from: number, to: number, duration: number): void {
  const existing = fadeFrames.get(audio);
  if (existing) cancelAnimationFrame(existing);
  const start = performance.now();
  const step = (now: number) => {
    const t = duration <= 0 ? 1 : Math.min(1, (now - start) / duration);
    const eased = t * (2 - t);
    audio.volume = Math.max(0, Math.min(1, from + (to - from) * eased));
    if (t < 1) {
      fadeFrames.set(audio, requestAnimationFrame(step));
    } else {
      fadeFrames.delete(audio);
    }
  };
  fadeFrames.set(audio, requestAnimationFrame(step));
}

export interface AmbiencePlayerApi {
  /** User preference; persisted. */
  isOn: boolean;
  /** Audio element's actual playback state. */
  isPlaying: boolean;
  /** Toggle on/off. Plays / fades-out as appropriate. */
  toggle(): void;
  /** Programmatic on/off (boot dismissal calls this with `false`). */
  setOn(on: boolean): void;
  /** Smooth fade-out to silence + pause. Used by the boot splash on dismiss. */
  fadeOut(durationMs?: number): void;
  /** Lower the bed under spoken narration. `level` is a fraction of the normal target (0.22 = 22%). */
  duck(level?: number, durationMs?: number): void;
  /** Restore the bed to its normal target after narration ends. */
  unduck(durationMs?: number): void;
  /** Try to resume playback (call after a user gesture if autoplay was blocked). */
  resume(): void;
  /** Send the bed back to its first frame and forget the saved playhead. A
   *  member reset is a client-side navigation, so the element keeps playing
   *  across it: clearing the stored position alone leaves the audio mid-track
   *  and lets it write the position back on the next pagehide. */
  rewind(): void;
  /** Warm up the switch-click element from a user gesture so a later
   *  programmatic play() isn't blocked by autoplay policy. */
  warmSwitchClick(): void;
  /** Play the switch-click. Safe to call outside a user gesture as
   *  long as `warmSwitchClick()` ran inside one earlier. */
  playSwitchClick(volume?: number): void;
  /**
   * Play the rising tones that run under the ignition ripples and hand off to
   * the switch-click, so the snap arrives as the end of something rather than
   * out of nowhere. Returns a cancel function; call it if the boot goes away
   * early, or the notes outlive the splash that motivated them.
   */
  playIgnitionSequence(): () => void;
}

const Ctx = createContext<AmbiencePlayerApi | null>(null);

export function AmbiencePlayerProvider({ children }: { children: ReactNode }) {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [isOn, setIsOnState] = useState<boolean>(() => readPref());
  const [isPlaying, setIsPlaying] = useState(false);

  // Tracks consumer intent (e.g. AmbientToggle on /onboarding calls
  // resume()). Kept as state, not a ref, so the effect below re-runs
  // when it flips and can arm the pointerdown fallback. Refs don't
  // trigger effects, so the previous version silently missed the
  // post-OAuth case where resume() runs after the provider's initial
  // mount and the listener was never attached.
  const [wantPlaying, setWantPlaying] = useState(false);

  const duckRef = useRef(1);

  // Memoized by hand: biome's exhaustive-deps does not model the React
  // Compiler, and this feeds an effect dep array below.
  const tryPlay = useCallback(async () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (!audio.src) {
      audio.src = AUDIO_SRC;
      const saved = readPosition();
      if (saved > 0) {
        audio.addEventListener(
          "loadedmetadata",
          () => {
            if (audio.duration > 0) audio.currentTime = saved % audio.duration;
          },
          { once: true },
        );
      }
    }
    try {
      audio.volume = 0;
      await audio.play();
      setIsPlaying(true);
      fadeVolume(audio, 0, TARGET_VOLUME * duckRef.current, FADE_IN_MS);
    } catch {
      // Autoplay blocked - the pointerdown fallback below picks it up.
    }
  }, []);

  useEffect(() => {
    if (!isOn || !wantPlaying || isPlaying) return;
    void tryPlay();
    const handler = () => {
      void tryPlay();
    };
    document.addEventListener("pointerdown", handler, { once: true });
    return () => document.removeEventListener("pointerdown", handler);
  }, [isOn, wantPlaying, isPlaying, tryPlay]);

  // Capture the playhead whenever the page goes away (OAuth redirect, reload,
  // tab close) or hides, so the next session's tryPlay resumes it.
  useEffect(() => {
    const save = () => savePosition(audioRef.current);
    const onVisibility = () => {
      if (document.visibilityState === "hidden") save();
    };
    window.addEventListener("pagehide", save);
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      window.removeEventListener("pagehide", save);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, []);

  const setOn = (next: boolean) => {
    setIsOnState(next);
    writePref(next);
    setWantPlaying(next);
    if (next) return;
    const audio = audioRef.current;
    if (!audio) return;
    fadeVolume(audio, audio.volume, 0, FADE_OUT_DEFAULT_MS);
    window.setTimeout(() => {
      audio.pause();
      setIsPlaying(false);
    }, FADE_OUT_DEFAULT_MS + 50);
  };

  const toggle = () => {
    setOn(!isOn);
  };

  const fadeOut = (durationMs = FADE_OUT_DEFAULT_MS) => {
    setWantPlaying(false);
    const audio = audioRef.current;
    if (!audio || audio.paused) return;
    fadeVolume(audio, audio.volume, 0, durationMs);
    window.setTimeout(() => {
      audio.pause();
      setIsPlaying(false);
    }, durationMs + 50);
  };

  const resume = () => {
    setWantPlaying(true);
  };

  // Stable identities: consumers put these in effect deps (the Aha narration
  // ducks on a beat effect and unducks in its cleanup). Recreating them each
  // render re-ran those effects and fired unduck() on every provider render,
  // which cancelled the duck the instant the bed started. Refs only, no deps.
  const duck = useCallback((level = 0.22, durationMs = 450) => {
    duckRef.current = Math.max(0, Math.min(1, level));
    const audio = audioRef.current;
    if (!audio || audio.paused) return;
    fadeVolume(audio, audio.volume, TARGET_VOLUME * duckRef.current, durationMs);
  }, []);
  const unduck = useCallback((durationMs = 700) => {
    duckRef.current = 1;
    const audio = audioRef.current;
    if (!audio || audio.paused) return;
    fadeVolume(audio, audio.volume, TARGET_VOLUME, durationMs);
  }, []);

  /*
   * The snap is a one-shot sound the boot splash fires on its iris snap.
   * Browsers reject a programmatic `.play()` made more than a few seconds
   * after the last user gesture, so we keep a single Audio element at the App
   * root and "prime" it from inside a user gesture (the "Take me to today"
   * click in onboarding) before the boot transition runs. Priming is a muted
   * play()+pause() cycle that registers the element with the autoplay policy
   * for the rest of the session.
   */
  const snapRef = useRef<HTMLAudioElement | null>(null);
  const snapPrimedRef = useRef(false);
  const ensureSnap = (): HTMLAudioElement | null => {
    if (snapRef.current) return snapRef.current;
    try {
      const a = new Audio(SNAP_SRC);
      a.preload = "auto";
      snapRef.current = a;
      return a;
    } catch {
      return null;
    }
  };
  const warmSwitchClick = () => {
    if (snapPrimedRef.current) return;
    const a = ensureSnap();
    if (!a) return;
    const prevVolume = a.volume;
    const prevMuted = a.muted;
    a.muted = true;
    a.volume = 0;
    void a
      .play()
      .then(() => {
        a.pause();
        a.currentTime = 0;
        a.muted = prevMuted;
        a.volume = prevVolume;
        snapPrimedRef.current = true;
      })
      .catch(() => {
        a.muted = prevMuted;
        a.volume = prevVolume;
      });
  };
  const rewind = () => {
    const a = audioRef.current;
    if (a) a.currentTime = 0;
    try {
      sessionStorage.removeItem(POSITION_KEY);
    } catch {
      // sessionStorage unavailable; the element rewind is the part that matters
    }
  };

  const playSwitchClick = (volume = 0.85) => {
    const a = ensureSnap();
    if (!a) return;
    try {
      a.currentTime = 0;
      a.volume = Math.max(0, Math.min(1, volume));
      a.muted = false;
      void a.play().catch(() => {
        a.muted = true;
        void a
          .play()
          .then(() => {
            a.muted = false;
          })
          .catch(() => {
            /* genuinely blocked; silent failure */
          });
      });
    } catch {
      /* ignore */
    }
  };

  /**
   * Synthesised rather than sampled: eight short notes is a handful of
   * oscillators, and an asset would be another file to ship and keep in sync
   * with the ripple timing it exists to match.
   */
  const audioCtxRef = useRef<AudioContext | null>(null);
  const playIgnitionSequence = (): (() => void) => {
    const noop = () => undefined;
    // The tones describe the ripples accelerating. Under reduced motion the
    // ripples do not animate, so there is nothing for them to describe.
    if (prefersReducedMotion()) return noop;
    const Ctor =
      typeof window !== "undefined"
        ? (window.AudioContext ??
          (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext)
        : undefined;
    if (!Ctor) return noop;
    try {
      const ctx = audioCtxRef.current ?? new Ctor();
      audioCtxRef.current = ctx;
      // Suspended when no gesture has reached this context yet. Onboarding is
      // all clicks, so this normally resolves; if it does not, the notes are
      // simply silent and the snap still lands.
      if (ctx.state === "suspended") void ctx.resume().catch(() => undefined);

      const started: OscillatorNode[] = [];
      const t0 = ctx.currentTime + 0.02;
      const voice = (
        type: OscillatorType,
        hz: number,
        at: number,
        peak: number,
        decaySec: number,
      ) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = type;
        osc.frequency.setValueAtTime(hz, at);
        gain.gain.setValueAtTime(0, at);
        gain.gain.linearRampToValueAtTime(peak, at + 0.02);
        gain.gain.exponentialRampToValueAtTime(0.0001, at + decaySec);
        osc.connect(gain).connect(ctx.destination);
        osc.start(at);
        osc.stop(at + decaySec + 0.05);
        started.push(osc);
      };

      for (const note of IGNITION_NOTES) {
        // Triangle keeps it warm; a sine is too pure to read over the bed and
        // a saw is a device alert.
        voice(
          "triangle",
          note.hz,
          t0 + note.atMs / 1000,
          IGNITION_PEAK_GAIN,
          IGNITION_NOTE_MS / 1000,
        );
      }
      const chimeAt = t0 + IGNITION_CHIME.atMs / 1000;
      const chimeDecay = IGNITION_CHIME.decayMs / 1000;
      voice("sine", IGNITION_CHIME.hz, chimeAt, IGNITION_CHIME.gain, chimeDecay);
      voice(
        "sine",
        IGNITION_CHIME.partialHz,
        chimeAt,
        IGNITION_CHIME.partialGain,
        chimeDecay * 0.55,
      );
      return () => {
        for (const osc of started) {
          try {
            osc.stop();
            osc.disconnect();
          } catch {
            /* already stopped */
          }
        }
      };
    } catch {
      return noop;
    }
  };

  const api: AmbiencePlayerApi = {
    isOn,
    isPlaying,
    toggle,
    setOn,
    fadeOut,
    duck,
    unduck,
    resume,
    rewind,
    warmSwitchClick,
    playSwitchClick,
    playIgnitionSequence,
  };

  return (
    <Ctx.Provider value={api}>
      {/* biome-ignore lint/a11y/useMediaCaption: ambient atmosphere loop, no spoken content */}
      <audio ref={audioRef} loop preload="none" />
      {children}
    </Ctx.Provider>
  );
}

export function useAmbience(): AmbiencePlayerApi {
  const ctx = use(Ctx);
  if (!ctx) {
    throw new Error("useAmbience must be called inside <AmbiencePlayerProvider>");
  }
  return ctx;
}

/** Like useAmbience, but tolerates rendering outside the provider (tests,
 *  isolated stories); consumers no-op on the ambience bed in that case. */
export function useAmbienceIfPresent(): AmbiencePlayerApi | null {
  return use(Ctx);
}
